import { CommonModule } from '@angular/common';
import { AfterViewInit, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { BaseModalComponent } from 'src/app/modal/base-modal';
import { ModalService } from 'src/app/modal/modal.service';
import { ProjectCalculationService } from 'src/app/services/project-calculation/project-calculation.service';
import { AppState } from 'src/app/state-management/app-state';
import * as CalculationActions from 'src/app/state-management/calculation-actions';
import { selectCalculationMeta } from 'src/app/state-management/calculation-selectors';
import { Language, Translation } from 'src/app/state-management/calculation-state';
import { DESCRIPTION_EXPORT_COLUMNS, EXTERNAL_OPEN_QUOTE_COLUMNS, EXTERNAL_QUOTE_COLUMNS, HOURS, INTERNAL_OPEN_QUOTE_COLUMNS, PAPER_SIZE, QUANTITIES_COLUMNS } from 'src/app/features/pdf-export/pdf-export-service.settings';
import { Branding } from 'src/app/features/pdf-export/types/pdf-cell.types';
import { PdfExportOptions } from 'src/app/features/pdf-export/types/pdf-export.types';

@Component({
    selector: 'app-pdf-export',
    imports: [MatFormFieldModule, MatSelectModule, MatInputModule, FormsModule, CommonModule, MatSlideToggleModule,
        MatIconModule,
        MatDividerModule,
        MatButtonModule],
    templateUrl: './pdf-export.component.html',
    styleUrl: './pdf-export.component.scss'
})
export class PdfExportComponent extends BaseModalComponent implements AfterViewInit {
    private unsubscribe$ = new Subject<void>();

    public readonly options: Record<string, Partial<PdfExportOptions>> = {
        'eb': {
            description: 'Externe begroting (standaard)',
            title: { nl: 'Offerte', en: 'Quotation' },
            columns: EXTERNAL_QUOTE_COLUMNS,
            paper: {
                size: PAPER_SIZE.A4,
                orientation: 'PORTRAIT'
            },
            includeTail: true
        },
        'mmw': {
            description: 'Meer-/minderwerk specificatie',
            title: { nl: 'Meer-/minderwerk specificatie', en: 'Specification of variations' },
            columns: EXTERNAL_QUOTE_COLUMNS,
            paper: {
                size: PAPER_SIZE.A4,
                orientation: 'PORTRAIT'
            },
            includeTail: true
        },
        'q': {
            description: 'Hoeveelheden',
            title: { nl: 'Hoeveelheden', en: 'Quantities' },
            columns: QUANTITIES_COLUMNS,
            paper: {
                size: PAPER_SIZE.A4,
                orientation: 'PORTRAIT'
            }
        },
        'eob': {
            description: 'Externe open begroting',
            title: { nl: 'Offerte', en: 'Quotation' },
            columns: EXTERNAL_OPEN_QUOTE_COLUMNS,
            paper: {
                size: PAPER_SIZE.A3,
                orientation: 'LANDSCAPE'
            }
        },
        'iob': {
            description: 'Interne open begroting',
            title: { nl: 'Offerte', en: 'Quotation' },
            columns: INTERNAL_OPEN_QUOTE_COLUMNS,
            paper: {
                size: PAPER_SIZE.A3,
                orientation: 'LANDSCAPE'
            }
        },
        'eu': {
            description: 'Externe uren',
            title: { nl: 'Externe uren', en: 'Quantities' },
            columns: HOURS,
            paper: {
                size: PAPER_SIZE.A4,
                orientation: 'PORTRAIT'
            }
        },
        'om': {
            description: 'Omschrijvingen',
            title: { nl: 'Omschrijvingen', en: 'Descriptions' },
            columns: DESCRIPTION_EXPORT_COLUMNS,
            paper: {
                size: PAPER_SIZE.A4,
                orientation: 'PORTRAIT'
            },
            includeTail: false
        },
    };

    public get optionKeys(): string[] {
        return Object.keys(this.options).sort();
    }

    public documentType = 'eb';

    // Create default PDF export options, based on the currently selected calculation
    public selectedOptions: PdfExportOptions = {
        description: 'Externe begroting (standaard)', // Default description
        branding: Branding.TO_BUILD,
        includeTerms: true,
        includeTail: true,
        includeRemarks: false,
        title: { nl: 'Offerte', en: 'Quotation' },
        columns: EXTERNAL_QUOTE_COLUMNS,
        paper: {
            size: PAPER_SIZE.A4,
            orientation: 'PORTRAIT'
        },
        margins: {
            left: 20,
            top: 20,
            right: 20,
            bottom: 20
        },
        language: Language.NL
    };

    constructor(
        private store: Store<AppState>,
        private modalService: ModalService,
        private projectCalculationService: ProjectCalculationService
    ) {
        super();

        this.title = 'Exporteer als PDF';
    }

    /**
     * Overriding init from base class
     * No input is needed since using the Redux store
     * This will give access to the active calculation
     * @param args 
     */
    public init(args: any) {
        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$),
        ).subscribe(async meta => {
            // console.log(meta);
            // Update the current options based on the calculation meta
            const options: Partial<PdfExportOptions> = {
                includeTerms: meta!.includeTerms,
                language: meta!.templateName!.includes('-en') ? Language.EN : Language.NL,
                branding: (await this.projectCalculationService.getProjectById(meta!.projectId!))?.projectGroup.startsWith('OFF') ? Branding.TO_BUILD_PROJECTS : Branding.TO_BUILD
            }

            // Update current options object
            this.selectedOptions = {
                ...this.selectedOptions,
                ...options
            };
        });
    }

    ngAfterViewInit(): void {
    }

    updateDocumentType(event: Event) {
        const selectedValue = (event.target as HTMLSelectElement).value;
        // console.log('Geselecteerde documenttype:', selectedValue);

        // Update the options to the defaults for this document type
        this.selectedOptions = {
            ...this.selectedOptions,
            ...this.options[selectedValue]
        };
    }

    async exportPdf() {
        // Init PDF export
        // Override 'add conditions' option based on project settings for now
        this.store.dispatch(CalculationActions.pdfExport({ options: this.selectedOptions }));

        this.modalService.close();
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

}
